import { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import paymentApi from 'api/rest/payment';
import EchoContext from 'context/EchoContext';
import uiSlice from 'store/modules/ui';
import authSlice from 'store/modules/auth';

/**
 * Hook para manejar el procesamiento de pagos con Wompi.
 *
 * Flujo:
 *   startMultiPlanProcessing(planIds)
 *   → backend genera referencia + firma
 *   → abre Wompi checkout URL en nueva pestaña
 *   → modal muestra "esperando confirmación..."
 *   → webhook crea ClientPayment → Pusher notifica → modal muestra "éxito"
 *
 * Redirect de vuelta:
 *   Wompi redirige a /payment-history?payment_gateway=wompi
 *   ClientPaymentList detecta el param y llama openProcessingModal()
 *   El modal espera el evento Pusher .payment.completed
 */
function usePaymentProcessing(userId) {
  const dispatch = useDispatch();
  const echoContext = useContext(EchoContext);
  const echo = echoContext?.echo || null;
  const [modalOpen, setModalOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const [timeRemaining, setTimeRemaining] = useState(300);
  const channelRef = useRef(null);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const currentPlanIdsRef = useRef([]);
  const currentCouponRef = useRef(null);

  const [showPaymentInfo, setShowPaymentInfo] = useState(true);

  useEffect(() => {
    const isProcessing = modalOpen && status === 'processing';
    dispatch(uiSlice.actions.setPaymentModalProcessing(isProcessing));
  }, [modalOpen, status, dispatch]);

  useEffect(() => {
    if (modalOpen && status === 'processing') {
      setTimeRemaining(300);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);

      timeoutRef.current = setTimeout(() => {
        setStatus('failed');
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      }, 300000);

      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);

      return () => {
        if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      };
    }
    return undefined;
  }, [modalOpen, status]);

  const modalListenerRef = useRef(null);
  useEffect(() => {
    modalListenerRef.current = (eventData) => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

      const sessionsToAdd = eventData?.total_sessions_paid || eventData?.sessions_paid || 0;
      if (eventData?.membership_status || sessionsToAdd) {
        dispatch(authSlice.actions.meSuccess({
          membership_status: eventData.membership_status,
          available_sessions: sessionsToAdd,
        }));
      }
      setStatus('success');
      dispatch(authSlice.actions.me('platform'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!echo || !userId || !modalOpen || status !== 'processing') return undefined;
    if (channelRef.current) return undefined;
    if (!echo.private || typeof echo.private !== 'function') return undefined;

    let channel;
    try {
      channel = echo.private(`users.${userId}`);
      channelRef.current = channel;
    } catch {
      return undefined;
    }

    const successWrapper = (eventData) => {
      if (modalListenerRef.current) modalListenerRef.current(eventData);
    };
    const failedWrapper = () => {
      if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      setStatus('failed');
    };
    channel.listen('.payment.completed', successWrapper);
    channel.listen('.payment.failed', failedWrapper);

    return () => {
      if (channelRef.current) {
        try {
          channelRef.current.stopListening('.payment.completed', successWrapper);
          channelRef.current.stopListening('.payment.failed', failedWrapper);
          channelRef.current = null;
        } catch {
          // silent
        }
      }
    };
  }, [echo, userId, modalOpen, status]);

  const openWompiCheckout = useCallback((txData) => {
    const redirectUrl = `${window.location.origin}/payment-history?payment_gateway=wompi`;
    const params = new URLSearchParams({
      'public-key': txData.public_key,
      currency: 'COP',
      'amount-in-cents': String(txData.amount_in_cents),
      reference: txData.reference,
      'signature:integrity': txData.signature,
      'redirect-url': redirectUrl,
    });
    // Mark that this tab already has the modal open so the redirect back doesn't re-open it
    sessionStorage.setItem('wompi_modal_active', '1');
    window.open(`https://checkout.wompi.co/p/?${params.toString()}`, '_blank');
  }, []);

  const startMultiPlanProcessing = useCallback(async (planIds, couponCode = null) => {
    if (!planIds || planIds.length === 0) return;

    currentPlanIdsRef.current = planIds;
    currentCouponRef.current = couponCode;
    setModalOpen(true);
    setStatus('processing');

    try {
      const txData = await paymentApi.createWompiTransaction({ plan_ids: planIds, coupon_code: couponCode });
      openWompiCheckout(txData);
    } catch {
      setStatus('failed');
    }
  }, [openWompiCheckout]);

  const startProcessing = useCallback(async (planId) => {
    await startMultiPlanProcessing([planId]);
  }, [startMultiPlanProcessing]);

  /**
   * Solo abre el modal en modo "processing" sin abrir ninguna pestaña.
   * Se usa cuando el usuario regresa del redirect de Wompi.
   * Retorna false si el modal ya estaba activo en esta sesión (evita re-abrir en redirect de Wompi).
   */
  const openProcessingModal = useCallback(() => {
    if (sessionStorage.getItem('wompi_modal_active') === '1') {
      return false;
    }
    setModalOpen(true);
    setStatus('processing');
    return true;
  }, []);

  const retryPayment = useCallback(async (planId) => {
    const planIds = planId ? [planId] : currentPlanIdsRef.current;
    setStatus('processing');
    try {
      const txData = await paymentApi.createWompiTransaction({ plan_ids: planIds, coupon_code: currentCouponRef.current });
      openWompiCheckout(txData);
    } catch {
      setStatus('failed');
    }
  }, [openWompiCheckout]);

  const closeModal = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

    sessionStorage.removeItem('wompi_modal_active');
    setModalOpen(false);
    setTimeout(() => {
      setStatus('idle');
      setTimeRemaining(300);
      currentPlanIdsRef.current = [];
    }, 300);
  }, []);

  const dismissPaymentInfo = useCallback(() => {
    setShowPaymentInfo(false);
  }, []);

  return {
    modalOpen,
    status,
    timeRemaining,
    showPaymentInfo,
    startProcessing,
    startMultiPlanProcessing,
    openProcessingModal,
    retryPayment,
    closeModal,
    dismissPaymentInfo,
  };
}

export default usePaymentProcessing;
